import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddShoppingCartRoundedIcon from '@mui/icons-material/AddShoppingCartRounded';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useAppContext } from '../../contexts/AppContext';
import { fetchProductDetails } from '../../services/homeDashboardService';
import { buildProductPath, slugify } from '../../utils/seo';
import './ProductDetails.scss';

export default function ProductDetails() {
  const navigate = useNavigate();
  const {
    user,
    cartItems,
    addToCart,
    decreaseFromCart,
    favoriteItems,
    addToFavorite,
    removeFromFavorite,
  } = useAppContext();
  const { productId, slug } = useParams();
  const [details, setDetails] = useState({
    product: null,
    images: [],
    variants: [],
  });
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isAddAnimating, setIsAddAnimating] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const userId = useMemo(
    () => user?.user_id ?? user?.id ?? user?.customer_id ?? '',
    [user],
  );

  useEffect(() => {
    let isMounted = true;

    const loadProductDetails = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetchProductDetails({
          productId,
          userId,
        });

        if (!isMounted) {
          return;
        }

        setDetails({
          product: response.product,
          images: response.images,
          variants: response.variants,
        });
        setSelectedImage(
          response.images[0]?.image || response.product?.image || '',
        );
        setSelectedVariantId(response.variants[0]?.variant_id || '');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProductDetails();

    return () => {
      isMounted = false;
    };
  }, [productId, userId]);

  const product = details.product;
  const galleryImages = details.images.length
    ? details.images
    : product?.image
      ? [{ image: product.image }]
      : [];
  const selectedVariant =
    details.variants.find(
      (variant) => String(variant.variant_id) === String(selectedVariantId),
    ) ||
    details.variants[0] ||
    null;
  const isFavorite = favoriteItems.some(
    (item) => String(item.id) === String(product?.product_id),
  );
  const selectedCartItemId =
    product && selectedVariant
      ? `${product.product_id}-${selectedVariant.variant_id}`
      : '';
  const selectedCartItem = cartItems.find(
    (item) => String(item.id) === String(selectedCartItemId),
  );
  const cartQuantity = selectedCartItem?.quantity || 0;

  useEffect(() => {
    if (!product?.name) {
      return undefined;
    }

    const expectedPath = buildProductPath(product);
    const currentSlug = slugify(slug);
    const expectedSlug = slugify(product.name);

    if (slug !== undefined && currentSlug !== expectedSlug) {
      navigate(expectedPath, { replace: true });
      return undefined;
    }

    if (slug === undefined && window.location.pathname !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }

    const previousTitle = document.title;
    const previousDescription =
      document
        .querySelector("meta[name='description']")
        ?.getAttribute('content') || '';
    const previousCanonical =
      document.querySelector("link[rel='canonical']")?.getAttribute('href') ||
      '';

    document.title = `${product.name} | Pappad App`;

    let metaDescription = document.querySelector("meta[name='description']");
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute(
      'content',
      product.description ||
        `Buy ${product.name} online with available size variants and quick checkout.`,
    );

    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }

    canonicalLink.setAttribute(
      'href',
      `${window.location.origin}${expectedPath}`,
    );

    return () => {
      document.title = previousTitle;
      metaDescription?.setAttribute('content', previousDescription);
      canonicalLink?.setAttribute('href', previousCanonical);
    };
  }, [navigate, product, slug]);

  const handleShare = async () => {
    if (navigator.share && product?.name) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
      } catch (error) {
        // no-op if user cancels share
      }
    }
  };

  const handleFavoriteToggle = () => {
    if (!product) {
      return;
    }

    const favoritePayload = {
      id: product.product_id,
      name: product.name,
      image: selectedImage || product.image,
      size: selectedVariant?.variant_name || '',
      price: selectedVariant?.price || 0,
      description: product.description,
    };

    if (isFavorite) {
      removeFromFavorite(product.product_id);
    } else {
      addToFavorite(favoritePayload);
    }
  };

  const handleAddToCart = () => {
    if (!product || !selectedVariant) {
      return;
    }

    setIsAddAnimating(true);
    const cartPayload = {
      id: `${product.product_id}-${selectedVariant.variant_id}`,
      product_id: product.product_id,
      variant_id: selectedVariant.variant_id,
      name: product.name,
      image: selectedImage || product.image,
      size: selectedVariant.variant_name,
      price: Number(selectedVariant.price || 0),
      description: product.description,
    };

    for (let index = 0; index < quantity; index += 1) {
      addToCart(cartPayload);
    }
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  useEffect(() => {
    if (!isAddAnimating) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsAddAnimating(false);
    }, 420);

    return () => window.clearTimeout(timeoutId);
  }, [isAddAnimating]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        {loadError.message}
      </Alert>
    );
  }

  if (!product) {
    return (
      <Typography sx={{ p: 2, color: 'text.secondary' }}>
        Product details are not available right now.
      </Typography>
    );
  }

  return (
    <Box className='product-details-page'>
      <Box className='product-details-shell'>
        <Box className='product-details-topbar'>
          <IconButton
            className='product-details-iconButton'
            onClick={() => navigate(-1)}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant='h5' className='product-details-title'>
            Details
          </Typography>
          <IconButton
            className='product-details-iconButton'
            onClick={handleShare}
          >
            <ShareOutlinedIcon />
          </IconButton>
        </Box>

        <Paper className='product-details-galleryCard'>
          <IconButton
            className='product-details-iconButton product-details-favorite'
            onClick={handleFavoriteToggle}
          >
            {isFavorite ? (
              <FavoriteRoundedIcon sx={{ color: '#ff5c67' }} />
            ) : (
              <FavoriteBorderRoundedIcon />
            )}
          </IconButton>
          <Box
            component='img'
            src={selectedImage || product.image}
            alt={product.name}
            className='product-details-mainImage'
          />
          {galleryImages.length ? (
            <Box className='product-details-thumbs'>
              {galleryImages.map((item, index) => {
                const isActive =
                  (selectedImage || product.image) === item.image;

                return (
                  <Box
                    key={`${item.image}-${index}`}
                    className={`product-details-thumb${
                      isActive ? ' product-details-thumb--active' : ''
                    }`}
                    onClick={() => setSelectedImage(item.image)}
                  >
                    <img
                      src={item.image}
                      alt={`${product.name} ${index + 1}`}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Paper>

        <Box className='product-details-headingRow'>
          <Box className='product-details-copy'>
            <Typography className='product-details-eyebrow'>
              Freshly packed snacks
            </Typography>
            <Typography variant='h5' className='product-details-name'>
              {product.name}
            </Typography>
            <Typography
              className={`product-details-kicker${
                isDescriptionExpanded ? ' product-details-kicker--expanded' : ''
              }`}
            >
              {product.description || 'Premium quality product'}
            </Typography>
            <Button
              className='product-details-readMore'
              onClick={() =>
                setIsDescriptionExpanded((currentValue) => !currentValue)
              }
            >
              {isDescriptionExpanded ? 'Show less' : 'Read more'}
            </Button>
          </Box>
          <Box className='product-details-priceWrap'>
            <Typography className='product-details-priceLabel'>
              Price
            </Typography>
            <Typography variant='h5' className='product-details-price'>
              Rs. {selectedVariant?.price || '0'}
            </Typography>
          </Box>
        </Box>

        <Typography className='product-details-sectionLabel'>
          Select Size
        </Typography>
        <Box className='product-details-variantRow'>
          <Box className='product-details-sizeOptions'>
            {details.variants.map((variant) => {
              const isActive =
                String(variant.variant_id) ===
                String(selectedVariant?.variant_id);

              return (
                <Chip
                  key={variant.variant_id}
                  label={variant.variant_name}
                  onClick={() => setSelectedVariantId(variant.variant_id)}
                  className={`product-details-sizeChip${
                    isActive ? ' product-details-sizeChip--active' : ''
                  }`}
                />
              );
            })}
          </Box>

          {/* <Box className='product-details-qtyWrap'>
            <IconButton
              className='product-details-qtyButton product-details-qtyButton--soft'
              onClick={() => setQuantity((value) => Math.max(1, value - 1))}
            >
              <RemoveRoundedIcon fontSize='small' />
            </IconButton>
            <Typography sx={{ fontWeight: 700, minWidth: 12, textAlign: 'center' }}>
              {quantity}
            </Typography>
            <IconButton
              className='product-details-qtyButton product-details-qtyButton--primary'
              onClick={() => setQuantity((value) => value + 1)}
            >
              <AddRoundedIcon fontSize='small' />
            </IconButton>
          </Box> */}
        </Box>

        {/* <Accordion className='product-details-accordion' defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#ff6d6d' }} />}
            className='product-details-accordionSummary'
          >
            <Typography className='product-details-sectionLabel'>
              Description
            </Typography>
          </AccordionSummary>
          <AccordionDetails className='product-details-accordionDetails'>
            <Typography className='product-details-description'>
              {product.description ||
                'A soft, breathable everyday essential designed for comfort and easy styling. Perfect for regular wear, layering, and quick casual outfits.'}
            </Typography>
          </AccordionDetails>
        </Accordion> */}

        <Box className='product-details-actions'>
          {cartQuantity > 0 ? (
            <Box
              className={`product-details-cartCounter app-pressable${
                isAddAnimating ? ' product-details-button--added' : ''
              }`}
            >
              <IconButton
                className='product-details-qtyButton product-details-qtyButton--soft'
                onClick={() => decreaseFromCart(selectedCartItemId)}
              >
                <RemoveRoundedIcon fontSize='small' />
              </IconButton>
              <Typography className='product-details-cartCount'>
                {cartQuantity}
              </Typography>
              <IconButton
                className='product-details-qtyButton product-details-qtyButton--primary'
                onClick={() =>
                  addToCart({
                    ...selectedCartItem,
                    id: selectedCartItemId,
                  })
                }
              >
                <AddRoundedIcon fontSize='small' />
              </IconButton>
            </Box>
          ) : (
            <Button
              className={`product-details-button product-details-button--ghost app-pressable${
                isAddAnimating ? ' product-details-button--added' : ''
              }`}
              startIcon={<AddShoppingCartRoundedIcon />}
              onClick={handleAddToCart}
            >
              Add To Cart
            </Button>
          )}
          <Button
            className='product-details-button product-details-button--primary app-pressable'
            startIcon={<LocalMallOutlinedIcon />}
            onClick={handleBuyNow}
          >
            Buy Now
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
